/**
 * BlockRenderer - Renders Gutenberg blocks as React components
 *
 * Renders WordPress HTML and React components.
 * Uses html-react-parser to preserve the layout structure (columns, groups).
 * Humanoid components are replaced in-place in the DOM tree.
 * Supports nested InnerBlocks via the data-has-inner-blocks attribute.
 */

import { ReactNode } from 'react';
import parse, { HTMLReactParserOptions, Element, domToReact, DOMNode } from 'html-react-parser';
import { componentLoader } from '@/services/component-loader';
import { decodeHtmlEntities } from '@/utils/decodeHtmlEntities';
import { ScriptEmbed } from '@/components/atoms/ScriptEmbed/ScriptEmbed';

// Tag names accepted by React's server renderer
// (cf. react-dom/server VALID_TAG_REGEX). A name outside this pattern crashes
// renderToString with "Invalid tag: …".
const VALID_TAG_NAME = /^[a-zA-Z][a-zA-Z0-9:_.-]*$/;

/**
 * Repairs the raw HTML of core/html blocks before parsing.
 *
 * Some embeds store a `<script>` whose space before the first attribute was
 * lost (`<scriptsrc="https://…">`). htmlparser2 then turns it into a tag with
 * an invalid name (`scriptsrc="https:`), which breaks SSR.
 * We reinsert the space: the only HTML tag starting with "script" is
 * "script".
 */
function normalizeBlockHtml(html: string): string {
  return html.replace(/<script(?=[^\s/>])/gi, '<script ');
}

export interface ParsedBlock {
  name: string;
  isComponent: boolean;
  attributes: Record<string, unknown>;
  innerHTML?: string;
}

interface BlockRendererProps {
  blocks: ParsedBlock[];
}

/**
 * Recursively parses a DOM node's children to turn them into React components.
 * Used for blocks with InnerBlocks.
 */
function parseInnerBlocks(children: DOMNode[], options: HTMLReactParserOptions): ReactNode {
  return domToReact(children, options);
}

/**
 * Creates the parser options with support for InnerBlocks.
 * The function is recursive to handle nested blocks.
 */
function createParserOptions(): HTMLReactParserOptions {
  const options: HTMLReactParserOptions = {
    replace: (domNode) => {
      // <script> tags from editorial content are injected client-side
      // (never rendered by renderToString) via ScriptEmbed.
      if (domNode instanceof Element && domNode.name === 'script') {
        const inline = (domNode.children?.[0] as { data?: string } | undefined)?.data;
        return <ScriptEmbed attribs={domNode.attribs} inline={inline} />;
      }

      // Safety net: any element with an invalid tag name is dropped, so a
      // malformed block never crashes SSR ("Invalid tag: …").
      if (domNode instanceof Element && domNode.name && !VALID_TAG_NAME.test(domNode.name)) {
        return <></>;
      }

      if (
        domNode instanceof Element &&
        domNode.attribs &&
        domNode.attribs['data-humanoid-block']
      ) {
        const componentName = domNode.attribs['data-component'];
        if (!componentName) return;

        const Component = componentLoader.getComponent(componentName);
        if (!Component) {
          console.warn('[BlockRenderer] Component not found:', componentName);
          return;
        }

        let props: Record<string, unknown> = {};
        const propsStr = domNode.attribs['data-props'];
        if (propsStr) {
          try {
            props = JSON.parse(decodeHtmlEntities(propsStr));
          } catch {
            console.warn('[BlockRenderer] Invalid props JSON:', propsStr);
          }
        }

        const hasInnerBlocks = domNode.attribs['data-has-inner-blocks'] === 'true';

        let children: ReactNode = null;
        if (hasInnerBlocks && domNode.children && domNode.children.length > 0) {
          children = parseInnerBlocks(domNode.children as DOMNode[], options);
        }

        // The actual props may live under props.props, depending on the PHP version
        const componentProps = props.props && typeof props.props === 'object'
          ? { ...props, ...props.props }
          : props;

        return (
          <div
            data-humanoid-block={domNode.attribs['data-humanoid-block']}
            data-component={componentName}
            data-namespace={domNode.attribs['data-namespace']}
            data-props={JSON.stringify(props)}
            data-ssr-rendered="true"
            data-has-inner-blocks={hasInnerBlocks ? 'true' : undefined}
          >
            <Component {...componentProps}>{children}</Component>
          </div>
        );
      }
    },
  };

  return options;
}

// Parser options, created once and reused across renders
const parserOptions = createParserOptions();

function SingleBlock({ block }: { block: ParsedBlock }) {
  if (!block.innerHTML) return null;

  // html-react-parser preserves the DOM tree structure
  const content = parse(normalizeBlockHtml(block.innerHTML), parserOptions);

  return <>{content}</>;
}

/**
 * BlockRenderer component
 *
 * Renders an array of parsed blocks from WordPress content.
 * Supports nested InnerBlocks for parent-child component relationships.
 *
 * @example
 * ```tsx
 * return <BlockRenderer blocks={blocks} />;
 * ```
 */
export function BlockRenderer({ blocks }: BlockRendererProps) {
  const validBlocks = blocks?.filter((block) => block.innerHTML) ?? [];

  if (validBlocks.length === 0) {
    return null;
  }

  return (
    <div className="blocks-content">
      {validBlocks.map((block, index) => (
        <SingleBlock key={`${block.name}-${index}`} block={block} />
      ))}
    </div>
  );
}

export default BlockRenderer;
